import React from "react";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import casestudy from "../../assets/images/casestudy_1.jpg";
export default function CamundaCaseStudy() {
  const caseStudies = [
    {
      id: "cs-01",
      category: "Fintech & Banking",
      readTime: "4 min read",
      title:
        "Migrating 140+ Core Payment Workflows to Camunda 8 with Zero Downtime",
      excerpt:
        "How a global financial institution bypassed legacy BPM lock-in, automated rule discovery with OrchestAI, and slashed infrastructure overhead by 42%.",
      slug: "/case-studies/payment-core-camunda-8",
      image: casestudy,
    },
    {
      id: "cs-02",
      category: "Healthcare Operations",
      readTime: "5 min read",
      title:
        "Automating Patient Prior Authorization with SMART-on-FHIR Connectors",
      excerpt:
        "Connecting disparate EHR silos into an event-driven Camunda 8 architecture, cutting clinical approval turnaround from 4 days to under 12 minutes.",
      slug: "/case-studies/fhir-prior-authorization",
    },
    {
      id: "cs-03",
      category: "Supply Chain & Logistics",
      readTime: "3 min read",
      title:
        "Real-Time Freight Exception Handling at Scale Using Zeebe Workers",
      excerpt:
        "Replacing legacy scheduled batch jobs with sub-second event-stream processing, cutting manual dispatch interventions by 65%.",
      slug: "/case-studies/freight-exception-camunda",
    },
  ];
  const [leadStory, ...secondaryStories] = caseStudies;
  return (
    <section className="w-full bg-[#F8FAFC] lg:py-24 md:py-16 py-10 px-6 sm:px-8 lg:px-12">
      <div className="3xl:w-4/5 xl:w-[90%] mx-auto space-y-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="inline-flex items-center gap-2 px-0.5 py-1.5 backdrop-blur-md shadow-xs mb-4"
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                CLIENT &amp; STORIES
              </span>
            </motion.div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
              Featured{" "}
              <span className="italic font-serif font-normal text-[#5dc9f0]">
                Case Studies
              </span>{" "}
            </h2>
          </div>
          <a
            href="#contact"
            className="group lg:w-auto w-60 relative inline-flex items-center justify-center md:justify-between gap-3 md:px-5 px-3 py-3 border border-black/15 bg-white/40 text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-lg rounded-full"
          >
            <span>View All Case Studies</span>
            <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
              <ArrowUpRight size={15} />
            </div>
          </a>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <article className="lg:col-span-7 group relative border overflow-hidden border-slate-200/90 rounded-xl p-6 flex flex-col justify-between hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 bg-white">
            <div className="relative z-10 space-y-6">
              <div className="relative w-full h-56 sm:h-64 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80">
                <img
                  src={leadStory.image}
                  alt={leadStory.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="font-bold text-xs uppercase tracking-wider text-white px-3 py-1 rounded-md shadow-xs ">
                    {leadStory.category}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#000036] tracking-tight leading-snug  transition-colors">
                  <a href="/capabilities/camunda">{leadStory.title}</a>
                </h3>
                <p className="text-sm sm:text-base text-slate-600 font-normal leading-relaxed">
                  {leadStory.excerpt}
                </p>
              </div>
            </div>
            <div className="relative z-10 pt-6 mt-6 border-t border-slate-100">
              <a
                href="/capabilities/camunda"
                className="group inline-flex items-center justify-between gap-3 px-6 py-2.5 border border-black/15 bg-white/70 text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-md rounded-full"
              >
                <span>Read Full Story</span>
                <div className="h-6 w-6 rounded-full  flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
                  <ArrowUpRight size={13} />
                </div>
              </a>
            </div>
          </article>
          <div className="lg:col-span-5 flex flex-col gap-8 justify-between">
            {secondaryStories.map((study) => (
              <article
                key={study.id}
                className="group relative border overflow-hidden border-slate-200/90 rounded-xl p-6 flex flex-col justify-between hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/40 transition-all duration-300 bg-white h-full"
              >
                <div className="relative z-10 space-y-4">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-[11px] font-bold uppercase  text-slate-700">
                      {study.category}
                    </span>
                    <span className="text-slate-400 font-medium">
                      {study.readTime}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-[#000036] tracking-tight leading-snug transition-colors">
                      <a href={study.slug}>{study.title}</a>
                    </h3>
                    <p className="text-sm text-slate-600 font-normal leading-relaxed line-clamp-3">
                      {study.excerpt}
                    </p>
                  </div>
                </div>
                <div className="relative z-10 pt-5 mt-5 border-t border-slate-100">
                  <a
                    href="/capabilities/camunda"
                    className="group inline-flex items-center justify-between gap-3 px-5 py-2 border border-black/15 bg-white/70 text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white rounded-full"
                  >
                    <span>Read Story</span>
                    <div className="h-5 w-5 rounded-full  flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
                      <ArrowUpRight size={12} />
                    </div>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
